// Clients / nodes view: a grid of client cards rendered at a single GLOBAL density
// (Compact | Standard | Full, persisted in localStorage) — replacing the old
// per-card "show containers" toggle. A card click opens a right-side DETAIL Drawer
// with the node profile, managed containers (provider instances), and Delete
// (DELETE, confirm). Registering a client lives behind a "+ New client" MODAL that
// reveals the one-time client token. Live heartbeat metrics override the snapshot.

import { useState } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useLiveReseed, clientsSig } from '../lib/useLiveReseed';
import { useNames } from '../lib/useNames';
import { groupByCategory, imageVersion, providerStateMeta } from '../lib/serviceMeta';
import { usePersistentState } from '../lib/usePersistentState';
import { useStore, useSeries, seriesKeys } from '../store/store';
import { useAuth } from '../auth/AuthContext';
import { Sparkline } from '../components/Sparkline';
import { Card, Loading, ErrorState, StatusBadge, Badge, ProgressBar, Segmented, bytesHuman } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { Modal, ConfirmDialog } from '../components/Modal';
import { Drawer, DetailRow } from '../components/Drawer';
import { friendly } from './Networks';
import { DOWNLOAD_ROUTE } from './Download';
import { ClientTokensSection } from './Tokens';
import type { Client, Network, ProviderInstance, NodeAdminService } from '../api/schemas';

type Density = 'compact' | 'standard' | 'full';

type LiveMetric = {
  cpu_pct?: number;
  ram_used_mb?: number;
  ram_total_mb?: number;
  gpus: { index: number; name: string; vram_total_mb: number; vram_free_mb: number; util_pct: number; temp_c: number }[];
  credits?: number;
  queue_depth?: number;
  store?: { bytes?: number; objects?: number; disk_free?: number; disk_total?: number };
};

// Full-mode node resource GRAPHS (ST39 series): small time-series line charts for
// cpu / ram / per-GPU util+VRAM that REPLACE the progress bars in Full density.
// Each graph carries a label + the current value; history comes from the live
// client.metrics bus frames the store rings into `series` (the same feed the cards
// poll/reseed from). With <2 samples the Sparkline draws a flat baseline while the
// header still shows the current reading, so the card is never empty.
function NodeGraphs({ clientId, cpuPct, ramPct, gpus }: { clientId: string; cpuPct: number; ramPct?: number; gpus: GpuView[] }) {
  const keys = [
    seriesKeys.cpu(clientId),
    seriesKeys.ram(clientId),
    ...gpus.flatMap((g) => [seriesKeys.gpuUtil(clientId, g.index), seriesKeys.gpuVram(clientId, g.index)]),
  ];
  const series = useSeries(keys);
  const byKey = new Map(series.map((s) => [s.key, s.points]));
  return (
    <div className="node-graphs">
      <div className="node-graph">
        <div className="node-graph-head">
          <label className="muted small">CPU / RAM %</label>
          <span className="small">
            CPU <b>{Math.round(cpuPct)}%</b>
            {ramPct != null && <> · RAM <b>{Math.round(ramPct)}%</b></>}
          </span>
        </div>
        <Sparkline
          width={320}
          height={48}
          min={0}
          max={100}
          fill
          series={[
            { points: byKey.get(seriesKeys.cpu(clientId)) ?? [], color: 'var(--accent, #4f8cff)' },
            { points: byKey.get(seriesKeys.ram(clientId)) ?? [], color: 'var(--warn, #d29922)' },
          ]}
          ariaLabel="cpu and ram history"
        />
      </div>
      {gpus.map((g) => {
        const vramPct = g.vram_total_mb ? Math.round(((g.vram_total_mb - g.vram_free_mb) / g.vram_total_mb) * 100) : 0;
        return (
          <div key={g.index} className="node-graph">
            <div className="node-graph-head">
              <label className="muted small">GPU{g.index} util / VRAM %</label>
              <span className="small">util <b>{g.util}%</b> · VRAM <b>{vramPct}%</b></span>
            </div>
            <Sparkline
              width={320}
              height={48}
              min={0}
              max={100}
              fill
              series={[
                { points: byKey.get(seriesKeys.gpuUtil(clientId, g.index)) ?? [], color: 'var(--ok, #3fb950)' },
                { points: byKey.get(seriesKeys.gpuVram(clientId, g.index)) ?? [], color: 'var(--accent, #4f8cff)' },
              ]}
              ariaLabel={`gpu ${g.index} util and vram history`}
            />
          </div>
        );
      })}
    </div>
  );
}

// Unified per-GPU view shape rendered by a card — sourced from either the live
// heartbeat (util_pct/temp_c) or the REST node_profile snapshot (util, no temp).
type GpuView = { index: number; name: string; vram_total_mb: number; vram_free_mb: number; util: number; temp_c?: number };

// DL2/DL3 — where "Download node client" goes: an INTERNAL route (<Download/>), not a URL off this
// app. The old NODE_CLIENT_URL pointed at a GitLab releases page on another host behind another
// login — the exact dead end the download page removed.
//
// The button itself is no longer HERE. It lived on this page because Nodes is what it is about, but
// that made a page-local answer to a question people have from anywhere, and it competed with the
// controls beside it either way — in the head-actions strip, or on a row underneath that pushed the
// node cards down. It is now pinned at the foot of the sidebar (Layout.tsx), which is reachable from
// every page including this one; the constant stays here as the name the rest of the app knows the
// route by.
export const NODE_CLIENT_PATH = DOWNLOAD_ROUTE;

export function Clients() {
  const { isAdmin } = useAuth();
  const res = useResource(() => api.clients(), []);
  const networks = useResource(() => api.networks(), []);
  const metrics = useStore((s) => s.metrics);
  // Live, not polled: reconcile the node list when a node goes online/offline (client.status) and on bus
  // (re)connect. Live heartbeat metrics (cpu/gpu/vram) already overlay from the store per card.
  useLiveReseed(res.reload, clientsSig);
  const [density, setDensity] = usePersistentState<Density>('mcpgraph.clients.density', 'standard');
  // Offline/stale nodes (no live/recent heartbeat → status 'offline') are hidden by default; the toggle
  // reveals them. Uses the same liveness signal the cards render (client.status, reconciled live).
  const [showOffline, setShowOffline] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [selected, setSelected] = useState<Client | null>(null);

  const allNodes = res.data?.items ?? [];
  const offlineCount = allNodes.filter((c) => c.status === 'offline').length;
  const visibleNodes = showOffline ? allNodes : allNodes.filter((c) => c.status !== 'offline');

  return (
    <div className="view">
      <div className="view-head">
        <h1>Nodes</h1>
        <div className="view-head-actions">
          <Segmented<Density>
            label="Density"
            value={density}
            onChange={setDensity}
            options={[
              { value: 'compact', label: 'Compact' },
              { value: 'standard', label: 'Standard' },
              { value: 'full', label: 'Full' },
            ]}
          />
          <label className="small muted" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }} title="Reveal nodes with no live/recent heartbeat">
            <input type="checkbox" checked={showOffline} onChange={(e) => setShowOffline(e.target.checked)} />
            Show offline{offlineCount > 0 ? ` (${offlineCount})` : ''}
          </label>
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New node</button>
        </div>
      </div>

      {revealed && <TokenReveal token={revealed} onDismiss={() => setRevealed(null)} />}

      {res.loading ? (
        <Loading what="Loading nodes…" />
      ) : res.error || !res.data ? (
        <ErrorState message={res.error ?? 'no data'} onRetry={res.reload} />
      ) : res.data.items.length === 0 ? (
        <Card><div className="empty">No nodes — register one with “+ New node”.</div></Card>
      ) : visibleNodes.length === 0 ? (
        <Card>
          <div className="empty">
            All {offlineCount} node{offlineCount === 1 ? ' is' : 's are'} offline.{' '}
            <button className="btn btn-sm" onClick={() => setShowOffline(true)}>Show offline</button>
          </div>
        </Card>
      ) : (
        <div className="client-grid">
          {visibleNodes.map((c) => (
            <ClientCard
              key={c.id}
              client={c}
              live={metrics[c.id]}
              density={density}
              onOpen={() => setSelected(c)}
            />
          ))}
        </div>
      )}

      {creating && (
        <RegisterClientModal
          networks={networks.data?.items ?? []}
          onClose={() => setCreating(false)}
          onCreated={(token) => {
            setCreating(false);
            setRevealed(token);
            res.reload();
          }}
        />
      )}

      {selected && (
        <ClientDrawer
          client={selected}
          live={metrics[selected.id]}
          onClose={() => setSelected(null)}
          onDeleted={() => {
            setSelected(null);
            res.reload();
          }}
        />
      )}

      {/* UX16 — the client tokens live with the nodes they authenticate, not on a
          tab that sorted them next to mcp-access grants by the word "token". Admin
          only, and a separate component rather than hidden markup: a member's page
          must issue no request that would 403. */}
      {isAdmin && <ClientTokensSection />}
    </div>
  );
}

function ClientCard({
  client,
  live,
  density,
  onOpen,
}: {
  client: Client;
  live?: LiveMetric;
  density: Density;
  onOpen: () => void;
}) {
  const np = client.node_profile;
  // Live heartbeat metrics (event bus) override the REST snapshot. The bus GPU
  // shape carries util_pct/temp_c; the REST snapshot carries `util`. Normalise.
  const gpus: GpuView[] = live?.gpus?.length
    ? live.gpus.map((g) => ({ index: g.index, name: g.name, vram_total_mb: g.vram_total_mb, vram_free_mb: g.vram_free_mb, util: g.util_pct, temp_c: g.temp_c }))
    : np.gpus.map((g) => ({ index: g.index, name: g.name, vram_total_mb: g.vram_total_mb, vram_free_mb: g.vram_free_mb, util: g.util, temp_c: g.temp_c }));
  const cpu = live?.cpu_pct ?? np.cpu ?? 0;
  // RAM: prefer the live heartbeat used/total; the REST snapshot only has total.
  const ramTotal = live?.ram_total_mb ?? np.ram ?? 0;
  const ramUsed = live?.ram_used_mb ?? 0;
  const host = np.labels?.host ?? np.hostname;
  const credits = live?.credits ?? client.credits ?? 0;
  const queue = live?.queue_depth ?? client.queue_depth ?? 0;
  // Node software version, when the node reports it (node_profile / labels). Optional.
  const nodeVersion = (np as { version?: string }).version || np.labels?.version || np.labels?.node_version;

  return (
    <div
      className="card clickable"
      onClick={onOpen}
      role="button"
      // Full cards carry ~2x the info (containers + tools), so give them 2x the
      // grid width in that mode only; compact/standard stay one column.
      style={density === 'full' ? { gridColumn: 'span 2' } : undefined}
    >
      <header className="card-head">
        <h2>
          <span className="client-title">{client.name} <StatusBadge status={client.status} /></span>
        </h2>
        <div className="card-actions"><span className="muted-row">credits <b>{credits}</b></span></div>
      </header>
      <div className="card-body">
        {/* Compact: no bars — just the essentials as text (queue + load). */}
        {density === 'compact' && (
          <div className="muted-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>queue <b>{queue}</b></span>
            <span>CPU <b>{Math.round(cpu)}%</b></span>
            {ramTotal > 0 && (
              <span>RAM <b>{ramUsed ? `${Math.round((ramUsed / ramTotal) * 100)}%` : bytesHuman(ramTotal * 1024 * 1024)}</b></span>
            )}
          </div>
        )}
        {density !== 'compact' && (
          <>
            <div className="muted-row">queue depth <b>{queue}</b></div>
            {/* Standard: progress bars for cpu/ram/vram. Full: time-series line graphs
                (ST39 series) REPLACE the bars — same metrics, historical instead of instantaneous. */}
            {density === 'full' ? (
              gpus.length > 0 || ramTotal > 0 || cpu > 0 ? (
                <NodeGraphs
                  clientId={client.id}
                  cpuPct={cpu}
                  ramPct={ramTotal > 0 && ramUsed > 0 ? (ramUsed / ramTotal) * 100 : undefined}
                  gpus={gpus}
                />
              ) : (
                <div className="muted">No GPUs reported (CPU node).</div>
              )
            ) : (
              <>
                <div className="metric-row">
                  <div className="metric">
                    <label>CPU</label>
                    <ProgressBar value={cpu / 100} />
                    <span>{Math.round(cpu)}%</span>
                  </div>
                  <div className="metric">
                    <label>RAM</label>
                    {ramTotal > 0 && ramUsed > 0 ? (
                      <>
                        <ProgressBar value={ramUsed / ramTotal} />
                        <span className="muted">{bytesHuman(ramUsed * 1024 * 1024)} / {bytesHuman(ramTotal * 1024 * 1024)}</span>
                      </>
                    ) : (
                      <span>{ramTotal ? bytesHuman(ramTotal * 1024 * 1024) : '—'}</span>
                    )}
                  </div>
                </div>
                {gpus.length > 0 ? (
                  gpus.map((g) => (
                    <div key={g.index} className="gpu-row">
                      <div className="gpu-head">
                        <span className="mono">GPU{g.index}{g.name ? ` · ${g.name}` : ''}</span>
                        <span>{g.util}%{g.temp_c != null ? ` · ${g.temp_c}°C` : ''}</span>
                      </div>
                      <label>VRAM</label>
                      <ProgressBar value={g.vram_total_mb ? (g.vram_total_mb - g.vram_free_mb) / g.vram_total_mb : 0} />
                      <span className="muted">{bytesHuman((g.vram_total_mb - g.vram_free_mb) * 1024 * 1024)} / {bytesHuman(g.vram_total_mb * 1024 * 1024)}</span>
                    </div>
                  ))
                ) : (
                  <div className="muted">No GPUs reported (CPU node).</div>
                )}
              </>
            )}
            {/* ST43 — node object-store / disk occupancy (from the heartbeat `store` block). */}
            {live?.store && (live.store.disk_total || live.store.bytes) != null && (
              <div className="node-disk muted-row small" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                {live.store.disk_total ? (
                  <span>disk <b>{bytesHuman((live.store.disk_total - (live.store.disk_free ?? 0)))}</b> / {bytesHuman(live.store.disk_total)} used</span>
                ) : null}
                {live.store.bytes != null && <span>store {bytesHuman(live.store.bytes)}</span>}
              </div>
            )}
          </>
        )}

        {density === 'full' && (
          <>
            <div className="client-meta">
              <span className="mono">{host ?? client.id}</span>
              {nodeVersion && <span className="chip" title="Node software version">node {nodeVersion}</span>}
              {Object.entries(np.labels).map(([k, v]) => (
                <span key={k} className="chip">{k}:{v}</span>
              ))}
              {np.driver && <span className="chip">drv {np.driver}</span>}
            </div>
            <ClientInstances clientId={client.id} />
          </>
        )}
      </div>
    </div>
  );
}

function ClientInstances({ clientId }: { clientId: string }) {
  const res = useResource(() => api.clientInstances(clientId), [clientId]);
  const names = useNames();
  if (res.loading) return <Loading what="Loading containers…" />;
  if (res.error || !res.data) return <ErrorState message={res.error ?? 'no data'} />;

  const cols: Column<ProviderInstance>[] = [
    {
      key: 'pub',
      header: 'Publication',
      cell: (i) => {
        // A node instantiates a service TEMPLATE into one or more containers. Show the
        // concrete instance ("<service> · <instance>") only when it's present AND differs
        // from the backing service-definition id (i.e. not the 1:1 case); else unchanged.
        // Synthetic rows (an idle-stopped/crashed service with no live provider) carry no
        // publication id — fall back to the service_definition_id so they still label sensibly.
        const pubId = i.service_publication_id;
        if (!pubId) {
          return <span className="mono small" title={i.service_definition_id ?? undefined}>{i.service_definition_id ?? '—'}</span>;
        }
        const sdid = names.publicationServiceDefId(pubId);
        const showInstance = i.instance && i.instance !== sdid;
        return (
          <span title={pubId}>
            {names.publicationLabel(pubId)}
            {showInstance && <span className="mono small"> · {i.instance}</span>}
          </span>
        );
      },
    },
    {
      key: 'state',
      header: 'State',
      // ST44: the RICH server-derived state (running/busy/warming/idle-stopped/failed/crashed/stale),
      // falling back to the coarse `runtime_state` when a gateway carries no detail. An idle-stopped
      // service reads as a neutral (not error) pill; only genuine failures/crashes are red.
      cell: (i) => {
        const meta = providerStateMeta(i.state ?? i.runtime_state);
        return <span title={meta.title}><Badge tone={meta.tone}>{meta.label}</Badge></span>;
      },
    },
    { key: 'cap', header: 'Capacity', align: 'right', cell: (i) => i.capacity },
    { key: 'vram', header: 'VRAM free', align: 'right', cell: (i) => (i.vram_free != null ? bytesHuman(i.vram_free * 1024 * 1024) : '—') },
    {
      key: 'tools',
      header: 'Tools',
      // Each tool is its own no-wrap chip: the list can wrap BETWEEN tools, but a
      // tool NAME is never broken across lines mid-word.
      cell: (i) => {
        const tools = i.canonical_tools ?? [];
        if (!tools.length) return <span className="muted">—</span>;
        return (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {tools.map((t) => (
              <span key={t} className="mono small" style={{ whiteSpace: 'nowrap' }}>{t}</span>
            ))}
          </span>
        );
      },
    },
  ];
  return (
    <div className="instances">
      <DataTable columns={cols} rows={res.data.items} rowKey={(i) => i.id ?? `${i.client_id}:${i.service_publication_id || i.service_definition_id || i.runtime_state}`} empty="No managed containers." />
    </div>
  );
}

function ClientDrawer({
  client,
  live,
  onClose,
  onDeleted,
}: {
  client: Client;
  live?: LiveMetric;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const np = client.node_profile;
  const credits = live?.credits ?? client.credits ?? 0;
  const queue = live?.queue_depth ?? client.queue_depth ?? 0;
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function del() {
    setBusy(true);
    setErr(null);
    try {
      await api.deleteClient(client.id);
      onDeleted();
    } catch (e) {
      setErr(friendly(e));
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={<span className="client-title">{client.name} <StatusBadge status={client.status} /></span>}
      subtitle={<code className="mono small">{client.id}</code>}
      onClose={onClose}
      actions={<button className="btn btn-sm btn-danger" onClick={() => setConfirmDel(true)}>Delete node</button>}
    >
      {err && <ErrorState message={err} />}
      <DetailRow label="Credits">{credits}</DetailRow>
      <DetailRow label="Queue depth">{queue}</DetailRow>
      {live?.store?.disk_total != null && (
        <DetailRow label="Disk">
          {bytesHuman(live.store.disk_total - (live.store.disk_free ?? 0))} / {bytesHuman(live.store.disk_total)} used
          {live.store.bytes != null && <span className="muted small"> · store {bytesHuman(live.store.bytes)}</span>}
        </DetailRow>
      )}
      {client.last_seen && <DetailRow label="Last seen">{new Date(client.last_seen).toLocaleString()}</DetailRow>}
      <DetailRow label="Host"><span className="mono small">{np.labels?.host ?? np.hostname ?? '—'}</span></DetailRow>
      {np.driver && <DetailRow label="Driver">{np.driver}</DetailRow>}
      <DetailRow label="Labels">
        <span className="client-meta">
          {Object.entries(np.labels).length
            ? Object.entries(np.labels).map(([k, v]) => <span key={k} className="chip">{k}:{v}</span>)
            : '—'}
        </span>
      </DetailRow>

      <NodeServicesEditor client={client} />

      <div>
        <h4>Managed containers</h4>
        <ClientInstances clientId={client.id} />
      </div>

      {confirmDel && (
        <ConfirmDialog
          title="Delete node"
          message={<>Deregister <b>{client.name}</b>? Its tokens and provider instances stop being routable.</>}
          confirmLabel="Delete node"
          busy={busy}
          error={err}
          onConfirm={del}
          onClose={() => setConfirmDel(false)}
        />
      )}
    </Drawer>
  );
}

// H8 — central node config (admin): assign which active services this node serves + worker limits,
// pushed to the node over its live control session. GPU-gated (services the node's largest GPU can't
// run are flagged and not assignable). Admin-only; hidden for non-admins.
function NodeServicesEditor({ client }: { client: Client }) {
  const { isAdmin } = useAuth();
  const res = useResource(() => api.nodeDesiredConfig(client.id), [client.id]);
  const [sel, setSel] = useState<Set<string> | null>(null);
  const [cpu, setCpu] = useState<string>('');
  const [credits, setCredits] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string[]>([]);

  if (!isAdmin) return null;
  if (res.loading) return <div><h4>Node services (admin)</h4><Loading what="Loading services…" /></div>;
  if (res.error || !res.data) return <div><h4>Node services (admin)</h4><ErrorState message={res.error ?? 'no data'} onRetry={res.reload} /></div>;

  const data = res.data;
  // Initialise the checkbox set from the node's ACTUAL served state, not just the gateway's
  // desired_config. A service can be live on the node without being in desired_config (e.g. baked
  // into the node's own client.yaml `services:`), so seeding from `selected` alone showed a running
  // service (echo) as unchecked — looking out-of-sync. `selected || live` makes the box reflect what
  // the node is really serving; saving then converges desired_config with reality (so it won't be
  // accidentally de-served). Once the admin edits, `sel` takes over and `live` no longer re-seeds.
  const selected = sel ?? new Set(data.services.filter((s) => s.selected || s.live).map((s) => s.id));
  const limits = (data.desired_config?.limits ?? {}) as { cpu_concurrency?: number; max_credits_per_gpu?: number };

  function toggle(s: NodeAdminService) {
    if (!s.gpu_satisfiable) return; // GPU-gated: cannot assign
    const next = new Set(selected);
    if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
    setSel(next);
  }

  async function apply() {
    setBusy(true);
    setMsg(null);
    setErr([]);
    try {
      const body: { selected: string[]; enabled: boolean; limits: { cpu_concurrency: number | null; max_credits_per_gpu: number | null } } = {
        selected: Array.from(selected),
        enabled: true,
        limits: {
          cpu_concurrency: cpu === '' ? (limits.cpu_concurrency ?? null) : Number(cpu),
          max_credits_per_gpu: credits === '' ? (limits.max_credits_per_gpu ?? null) : Number(credits),
        },
      };
      const r = await api.setNodeDesiredConfig(client.id, body);
      if (!r.ok) {
        setErr(r.errors.length ? r.errors : ['rejected']);
      } else {
        setMsg(r.delivered ? 'Applied and pushed to the live node.' : 'Saved — node offline, will apply on reconnect.');
        setSel(null);
        res.reload();
      }
    } catch (e) {
      setErr([friendly(e)]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="node-services-editor">
      <h4>Node services (admin)</h4>
      <p className="small muted">
        Assign which active services this node serves. Changes are pushed to the node live; assigned services come up and advertise.
        {data.node_max_vram_mb > 0 ? ` Largest GPU: ${Math.round(data.node_max_vram_mb / 1024)}GB.` : ' CPU-only node.'}
        {data.online ? ' Online.' : ' Offline (changes apply on reconnect).'}
      </p>
      {data.services.length === 0 ? (
        <div className="empty">No active services available for this node's owner.</div>
      ) : (
        <ul className="node-service-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {/* Grouped by category, each group + its services sorted alphabetically. */}
          {groupByCategory(data.services, (s) => s.tags, (s) => s.name).map(([cat, list]) => (
            <li key={cat} className="node-service-group">
              <div className="node-service-cat" style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, margin: '8px 0 2px' }}>
                {cat} <span className="muted">({list.length})</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {list.map((s) => (
                  <li key={s.id} className="node-service-row" data-service={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <input
                      type="checkbox"
                      aria-label={`assign ${s.name}`}
                      checked={selected.has(s.id)}
                      disabled={!s.gpu_satisfiable || busy}
                      onChange={() => toggle(s)}
                    />
                    <span className="node-service-name">{s.name}</span>
                    <span className="chip small" title={s.image_ref}>v: {imageVersion(s.image_ref)}</span>
                    <span className="chip small">{s.kind}</span>
                    {s.min_vram_gb > 0 && <span className="chip small">{s.min_vram_gb}GB</span>}
                    {!s.gpu_satisfiable && <span className="chip small chip-danger" title="Node GPU too small">won't fit</span>}
                    {s.live && <span className="chip small chip-success" title="Currently advertised live">live</span>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      <div className="node-limits" style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <label className="field small">CPU concurrency
          <input type="number" min={1} placeholder={String(limits.cpu_concurrency ?? '')} value={cpu} onChange={(e) => setCpu(e.target.value)} style={{ width: 80 }} />
        </label>
        <label className="field small">Credits / GPU
          <input type="number" min={0} placeholder={String(limits.max_credits_per_gpu ?? '')} value={credits} onChange={(e) => setCredits(e.target.value)} style={{ width: 80 }} />
        </label>
      </div>
      {err.length > 0 && <div className="form-error">{err.join('; ')}</div>}
      {msg && <div className="form-ok small" style={{ color: 'var(--ok, green)' }}>{msg}</div>}
      <div style={{ marginTop: 8 }}>
        <button className="btn btn-sm btn-primary" disabled={busy} onClick={apply}>
          {busy ? 'Applying…' : 'Apply to node'}
        </button>
      </div>
    </div>
  );
}

// Network multi-select (slugs) for the register form. Empty = none.
function NetworkSelect({ networks, value, onChange }: { networks: Network[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <select multiple value={value} onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))}>
      {networks.map((n) => (
        <option key={n.id} value={n.slug}>{n.name}</option>
      ))}
    </select>
  );
}

// Register a NEW client (console / GUI node). The server mints its first token,
// revealed once via onCreated. Lives in a modal opened from the view header.
function RegisterClientModal({
  networks,
  onClose,
  onCreated,
}: {
  networks: Network[];
  onClose: () => void;
  onCreated: (token: string) => void;
}) {
  const [name, setName] = useState('');
  const [sel, setSel] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api.createClient({ name: name.trim(), allowed_networks: sel });
      onCreated(res.client_token);
    } catch (ex) {
      setErr(friendly(ex));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Register a node"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={busy || !name.trim()} onClick={submit}>
            {busy ? 'Registering…' : 'Register node'}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="issue-form">
        <label className="field">Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="gpu-node-1" required />
        </label>
        <label className="field">Allowed networks (optional)
          <NetworkSelect networks={networks} value={sel} onChange={setSel} />
        </label>
        {err && <div className="form-error">{err}</div>}
      </form>
    </Modal>
  );
}

// One-time node-token reveal (server keeps only a hash). Shown after register.
function TokenReveal({ token, onDismiss }: { token: string; onDismiss: () => void }) {
  return (
    <Card title="New node token — copy it now (shown once)">
      <div className="token-reveal">
        <code className="mono">{token}</code>
        <button className="btn btn-sm" onClick={() => navigator.clipboard?.writeText(token)}>Copy</button>
        <button className="btn btn-sm" onClick={onDismiss}>Dismiss</button>
      </div>
      <p className="small muted">Put this in the console client's config (token: !secret …) — it won't be shown again.</p>
    </Card>
  );
}
